import { Injectable } from "@angular/core";
import { HttpHeaders } from "@angular/common/http";
import { InmatchPermission, localUserInfo, ParticipantRoles } from "../../../api";
import { User } from "../models/user";
import { Observable } from "rxjs";

const getUsernameAbbreviation = (username: string): string => {
  if (!username || username.indexOf("@") === -1) return username;

  const usernameParts = username.split("@");
  const parts = usernameParts[0].split(".");
  const partLength = parts.length === 1 ? 2 : 1;
  const abbreviatedUsername = parts.reduce(
    (acc, current) => acc + current.substr(0, partLength).toUpperCase(),
    ""
  );
  return `${abbreviatedUsername}@${usernameParts[1]}`;
};

export interface LoginData {
  token: string;
  id: string;
  email: string;
  user_permissions: string;
  username: string;
  user_type: string;
  organization_id: string;
  agreed_to_terms: string;
  http_status: number;
}

@Injectable({
  providedIn: "root"
})
export abstract class AuthService {
  private loggedInStatus = JSON.parse(
    localStorage.getItem("loggedIn") || "false"
  );
  private token: string;

  user: User;
  abstract readonly user$: Observable<User>;

  constructor() {}

  setToken(token: string) {
    this.token = token;
    localStorage.setItem("token", token);
    localUserInfo.fetchAttrs();
  }

  getToken() {
    return this.token;
  }

  setLoginData(loginData: LoginData) {
    this.loggedInStatus = true;
    localStorage.setItem("loggedIn", "true"); 
    localStorage.setItem("token", loginData.token);
    localStorage.setItem("id", loginData.id);
    localStorage.setItem("user_permissions", loginData.user_permissions);
    localStorage.setItem("username", loginData.username);
    localStorage.setItem("user_type", loginData.user_type);
    localStorage.setItem("organization_id", loginData.organization_id);
    localStorage.setItem("agreed_to_terms", loginData.agreed_to_terms);
    localStorage.setItem("username_abbr", getUsernameAbbreviation(loginData.username));
    localUserInfo.fetchAttrs();
  }

  removeLocalStorage() {
    localStorage.clear();
    localUserInfo.relationships_user_info = null;
  }

  getUserId() {
    return localStorage.getItem("id");
  }

  getUsername() {
    return localStorage.getItem("username");
  }

  getUsernameAbbreviation() {
    return (
      localStorage.getItem("username_abbr") ||
      getUsernameAbbreviation(localStorage.getItem("username"))
    );
  }

  getUserType() {
    return localStorage.getItem("user_type");
  }

  getCustId() {
    return localStorage.getItem("organization_id");
  }

  getAgreedToTerms() {
    const terms = localStorage.getItem("agreed_to_terms");
    return terms === "true";
  }

  setAgreeToTerms() {
    // This happens after someone agrees to the terms
    localStorage.setItem("agreed_to_terms", "true");
    return true;
  }

  isLoggedIn() {
    return (
      localStorage.getItem("loggedIn") &&
      localStorage.getItem("loggedIn") === "true"
    );
  }

  canAdmin() {
    return localStorage.getItem("user_permissions") === "admin";
  }

  getHttpOptions() {
    const token = localStorage.token || "";

    const httpOptions = {
      headers: new HttpHeaders({
        Authorization: "Bearer " + token
      })
    };

    return httpOptions;
  }

  getAccessTokenFromCache(): string {
    return localStorage.getItem('b2c_access_token');
  }

  saveAccessTokenToCache(accessToken: string): void {
    localStorage.setItem('b2c_access_token', accessToken);
  };

  isAdmin() {
    return localUserInfo.hasRole('Admin');
  }

  isSupplier() {
    return localUserInfo.hasRole('Supplier');
  }

  isCustomer() {
    return localUserInfo.hasRole('Customer');
  }

  isLandingPageVisible() {
    return !this.isAdmin() &&
        localUserInfo.hasRoleWithPermissions("InvoiceManagement", ParticipantRoles.Supplier, ["InvoiceManagementSupplierSupersupplier", 'InvoiceManagementSupplierWrite', 'InvoiceManagementSupplierApprover']) ||
        localUserInfo.hasRoleWithPermissions("Inmatch", ParticipantRoles.Supplier, [InmatchPermission.SupplierManager, InmatchPermission.SupplierAnalyst])
  }

  get userAccessDetails() {
    return this.user && this.user.userAccessDetails;
  }

  private hasRelationships() {
    return localUserInfo &&
    localUserInfo.relationships_user_info &&
    localUserInfo.relationships_user_info.length;
  }

  abstract ensureLoggedIn(): Promise<any>;

  abstract msalLogout();
}
